import { ForwardRefRenderFunction, forwardRef, ChangeEvent } from 'react';
import { InputField } from 'ht-styleguide';

/**
 * EmailFieldWithValidation Component
 *
 * This component is specifically designed for email address inputs, integrating validation and soft warning functionality.
 * It is optimized for use with the `useExternalEmailCheck` hook, which provides validation logic and soft warnings.
 *
 * When a `softWarning` is provided and no other errors are present, the component displays a clickable suggestion for a
 * potentially correct email. Clicking this suggestion invokes the `useSuggestion` function from `useExternalEmailCheck`,
 * which updates the email field with the suggested value.
 *
 * The component is also designed to work seamlessly with Formik for form state management, particularly handling values
 * and errors related to the email field.
 *
 * Props:
 * - `error`: A string representing the validation error message for the email field. This can come from Formik's
 *   validation or custom validation logic.
 * - `softWarning`: A string containing a suggested email address, typically provided by an external validation service
 *   via `useExternalEmailCheck`.
 * - `useSuggestion`: A function to apply the suggested email to the input field, provided by `useExternalEmailCheck`.
 * - Additional props (such as `name`, `placeholder`, `onChange`, `value`) are spread to the underlying `InputField`
 *   component, aligning with standard input field behavior.
 *
 * Example Usage:
 * <EmailFieldWithValidation
 *   name="email"
 *   placeholder="Enter your email address"
 *   onChange={handleOnChange} // Function from `useExternalEmailCheck`
 *   value={formik.values.email} // Value managed by Formik
 *   error={formik.errors.email || customError} // Error from Formik or custom logic
 *   softWarning={emailValidationState?.did_you_mean} // Suggested email from `useExternalEmailCheck`
 *   useSuggestion={useSuggestion} // Function from `useExternalEmailCheck`
 * />
 */

type TEmailFieldWithValidationProps = {
  error: string;
  softWarning: string;
  containerClass: string;
  useSuggestion: BaseAnyFunction;
  inputWrapperClass?: string;
  type: string;
  label?: string;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  placeholder: string;
};

const EmailFieldWithValidate: ForwardRefRenderFunction<HTMLInputElement, TEmailFieldWithValidationProps> = (
  { error, softWarning, useSuggestion = () => {}, ...props },
  ref,
) => (
  <div>
    <InputField ref={ref} error={error} {...props} autoComplete="new-password" />
    {softWarning && !error && (
      <button className="plainButton n700 caption" onClick={useSuggestion} type="button">
        <>
          Did you mean: <span className="underline">{softWarning}</span>?
        </>
      </button>
    )}
  </div>
);

export const EmailFieldWithValidation = forwardRef(EmailFieldWithValidate);
